import { useCallback, useEffect, useState } from 'react';
import { adminSettings, adminUpdateSettings } from '@suangua/shared';
import { PageHeader, LoadingRow, useAdminGuard } from '../parts';

/**
 * 管理端 · 全站设置：发帖 / 回复开关（即时保存，关闭后对应用户操作 → 403）。
 */
export function SettingsPage() {
  const guard = useAdminGuard();
  const [settings, setSettings] = useState<Record<string, string> | null>(null);
  const [error, setError] = useState('');
  const [tip, setTip] = useState('');
  const [pendingKey, setPendingKey] = useState('');

  const load = useCallback(() => {
    setError('');
    return adminSettings()
      .then((r) => setSettings(r.settings))
      .catch((e) => setError(guard(e)));
  }, [guard]);

  useEffect(() => {
    void load();
  }, [load]);

  /** 切换开关（即时保存；失败回滚并提示） */
  async function toggle(key: string) {
    if (!settings) return;
    const next = settings[key] === '1' ? '0' : '1';
    setSettings({ ...settings, [key]: next });
    setTip('');
    setError('');
    setPendingKey(key);
    try {
      await adminUpdateSettings({ [key]: next as '0' | '1' });
      setTip('已保存');
    } catch (e) {
      setSettings({ ...settings }); // 回滚
      setError(guard(e));
    } finally {
      setPendingKey('');
    }
  }

  if (error && !settings) {
    return <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">{error}</p>;
  }
  if (!settings) return <LoadingRow />;

  return (
    <div className="max-w-lg">
      <PageHeader title="全站设置" desc="即时保存；关闭后对应用户操作将被拒绝（403）" />

      {error && <p className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">{error}</p>}
      {tip && <p className="mb-3 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700">{tip}</p>}

      <SettingToggle
        title="全站发帖"
        desc="关闭后所有用户发帖接口返回 403（管理员后台不受影响）"
        on={settings.site_can_post === '1'}
        pending={pendingKey === 'site_can_post'}
        onChange={() => void toggle('site_can_post')}
      />
      <SettingToggle
        title="全站回复"
        desc="关闭后所有回复接口返回 403（管理员后台不受影响）"
        on={settings.site_can_comment === '1'}
        pending={pendingKey === 'site_can_comment'}
        onChange={() => void toggle('site_can_comment')}
      />
    </div>
  );
}

function SettingToggle(props: { title: string; desc: string; on: boolean; pending: boolean; onChange: () => void }) {
  return (
    <div className="flex items-center justify-between rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
      <div>
        <p className="text-sm font-medium text-stone-700">{props.title}</p>
        <p className="mt-0.5 text-xs text-stone-400">{props.desc}</p>
      </div>
      <button
        onClick={props.onChange}
        disabled={props.pending}
        className={`h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-60 ${
          props.on ? 'bg-green-500' : 'bg-stone-200'
        }`}
        aria-label={props.title}
      >
        <span
          className={`block h-5 w-5 translate-x-0.5 rounded-full bg-white shadow transition-transform ${
            props.on ? 'translate-x-5' : ''
          }`}
        />
      </button>
    </div>
  );
}
